import {Link} from "@tanstack/react-router";
import dayjs from "dayjs";

/**
 * Whether players can sign up through the event's link right now, said the same way next to every
 * copy of the link the organiser sees. The server's registrationPhase() (HasRegistrationPhase) is
 * the answer wherever the event carries it; the create wizard, which has only the dates it is
 * editing, reads the same rule from them: open from the registration start, until its finish.
 */
export type RegistrationLinkState = {
    tone: 'open' | 'soon' | 'closed';
    text: string;
    /** Closed only for want of dates: the organiser can open it by setting them. */
    needsDates: boolean;
    /** Off on purpose: the organiser turned registration off, rather than leaving dates unset. */
    off?: boolean;
};

type EventDates = {
    /** ForcedPrivate (4): registration switched off (Setup → Registration). */
    visibility_type?: number | string | null;
    registration_phase?: string | null;
    entry_starts_at?: string | null;
    entry_ends_at?: string | null;
};

const OPEN_PHASES = ['register', 'full', 'register_live'];
const STARTED_PHASES = ['generating_draws', 'happening', 'finished'];

export const formatRegistrationDate = (iso: string): string => dayjs(iso).format('D MMM YYYY, HH:mm');

export function registrationStateOf(event: EventDates, now: Date = new Date()): RegistrationLinkState {
    if (+(event.visibility_type ?? 0) === 4) {
        return {tone: 'closed', text: 'Off — players can only be added by you', needsDates: false, off: true};
    }
    const start = event.entry_starts_at ? dayjs(event.entry_starts_at) : null;
    const end = event.entry_ends_at ? dayjs(event.entry_ends_at) : null;
    const phase = event.registration_phase
        ?? (start && !dayjs(now).isBefore(start) && (!end || !dayjs(now).isAfter(end)) ? 'register'
            : start && dayjs(now).isBefore(start) ? 'not_open'
                : 'closed');

    if (OPEN_PHASES.includes(phase)) {
        return {tone: 'open', text: end ? `Open until ${formatRegistrationDate(end.toISOString())}` : 'Open', needsDates: false};
    }
    if (phase === 'not_open' && start && dayjs(now).isBefore(start)) {
        return {tone: 'soon', text: `Opens ${formatRegistrationDate(start.toISOString())}`, needsDates: false};
    }
    if (STARTED_PHASES.includes(phase)) {
        return {tone: 'closed', text: 'Closed — the event has started', needsDates: false};
    }

    return {tone: 'closed', text: 'Closed — set registration dates to open', needsDates: true};
}

const LOCAL_INPUT = 'YYYY-MM-DDTHH:mm';

/**
 * The window a new event's registration opens with, as datetime-local values: from now, until the
 * end of the day before the event starts, or a week from now when there is no start date yet (or
 * the event starts too soon for a day before it to come after now).
 */
export function defaultRegistrationWindow(eventStartLocal: string | null | undefined, now: Date = new Date()): {start: string; end: string} {
    const opens = dayjs(now);
    const eventStart = eventStartLocal ? dayjs(eventStartLocal) : null;
    const dayBefore = eventStart?.isValid() ? eventStart.subtract(1, 'day').hour(23).minute(59) : null;
    const closes = dayBefore && dayBefore.isAfter(opens) ? dayBefore : opens.add(7, 'day').hour(23).minute(59);

    return {start: opens.format(LOCAL_INPUT), end: closes.format(LOCAL_INPUT)};
}

/** Why a registration window cannot be saved as it stands, or null. Both dates are required. */
export function registrationDatesProblem(startLocal: string | null | undefined, endLocal: string | null | undefined): string | null {
    if (!startLocal || !endLocal) {
        return 'Set both a registration start and finish date — registration stays closed without them.';
    }
    if (!dayjs(endLocal).isAfter(dayjs(startLocal))) {
        return 'The registration finish must come after its start.';
    }

    return null;
}

/**
 * What a player is told after signing up: in at once (SelfEntryApproval approved it), or a
 * request the organiser confirms — "Require my approval for sign-ups", or a cap that held it.
 */
export const REQUEST_SENT = 'Request sent — the organiser will confirm your place';

export function signUpResultMessage(res: {approved?: boolean; applications?: {approved?: boolean}[]} | null | undefined, payOnTheDay = false): string {
    const approved = !!res?.approved || !!res?.applications?.some((a) => a.approved);
    if (approved) {
        return payOnTheDay ? "You're in! Pay the entry fee to the organiser on the day." : "You're in — your place is confirmed";
    }

    return payOnTheDay ? `${REQUEST_SENT}. The entry fee is paid on the day.` : REQUEST_SENT;
}

const DOT: Record<RegistrationLinkState['tone'], string> = {
    open:   'bg-success',
    soon:   'bg-warning',
    closed: 'bg-danger',
};

/** The state, one line, under or beside a copy of the sign-up link. */
export function RegistrationStateNote({event, setupHref, className = ''}: {event: EventDates; setupHref?: string; className?: string}) {
    const state = registrationStateOf(event);

    return (
        <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs ${className}`} data-testid="registration-state">
            <span className={`inline-block h-2 w-2 rounded-full ${DOT[state.tone]}`} aria-hidden />
            <span className={state.tone === 'closed' ? 'font-medium text-danger' : 'text-default-600'}>Registration: {state.text}</span>
            {(state.needsDates || state.off) && setupHref ? (
                <Link to={setupHref as any} className="font-medium text-primary underline">{state.off ? 'Turn registration on' : 'Set registration dates'}</Link>
            ) : null}
        </div>
    );
}
